/**
 * Notifications tab — choose which alerts you want, for this workspace.
 *
 * Everything starts off. The product can already tell that an ad's CPA tripled,
 * but volunteering that by mail to everyone would be a decision made on the
 * reader's behalf, and the readers are not alike: an AE watching one client
 * wants all four, a director watching eight wants none of it.
 *
 * SAVES ON TOGGLE, with no Save button. Four independent switches behind one
 * button invites the half-finished state where the screen shows what you chose
 * and the database does not. On failure the switch goes back to what the
 * database actually holds, so the screen never claims a setting that is not
 * stored.
 *
 * The workspace is named in the copy on purpose. These toggles are per
 * workspace, and that is invisible otherwise — someone who switched alerts on
 * for one client would reasonably assume they had switched them on everywhere.
 *
 * @module components/settings/NotificationsPanel
 */

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Bell } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { isSupabaseConfigured } from "@/config/supabase";
import {
  fetchAlertPreferences,
  saveAlertPreferences,
  ALERTS_OFF,
} from "@/services/notifications/notificationPreferencesService";
import type { AlertPreferences } from "@/services/notifications/digestBuilder";

/** The four switches, in the order they are shown. */
const ALERT_KEYS = [
  "alertCpaWorse",
  "alertNoConversions",
  "alertOverPlan",
  "alertConnection",
] as const satisfies readonly (keyof AlertPreferences)[];

/** i18n suffix per switch — `settings.notifications.<slug>Title` / `…Detail`. */
const SLUG: Record<keyof AlertPreferences, string> = {
  alertCpaWorse: "cpaWorse",
  alertNoConversions: "noConversions",
  alertOverPlan: "overPlan",
  alertConnection: "connection",
};

/**
 * What went wrong, without the message already translated.
 *
 * Holding a translated string in state would mean calling `t` inside the fetch
 * effect, which puts `t` in its dependency array — and `t` is not guaranteed to
 * be referentially stable, so the effect re-runs on every render and refetches
 * forever. Keeping the reason abstract and translating at render time removes
 * the dependency instead of trying to keep it honest.
 */
type PanelError = { kind: "load" } | { kind: "save" } | { kind: "message"; text: string };

interface ToggleProps {
  checked: boolean;
  disabled: boolean;
  label: string;
  detail: string;
  onChange: (next: boolean) => void;
}

const Toggle: React.FC<ToggleProps> = ({ checked, disabled, label, detail, onChange }) => (
  // A real checkbox, visually restyled: it keeps the label association, the
  // space key, and the focus ring that a div-with-onClick would all have to
  // reimplement.
  <label
    className={`flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${
      disabled
        ? "border-neutral-200 bg-neutral-50 cursor-not-allowed"
        : "border-neutral-200 bg-white hover:border-neutral-300 cursor-pointer"
    }`}
  >
    <input
      type="checkbox"
      className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-ink-950 focus:ring-2 focus:ring-ink-950 focus:ring-offset-1 disabled:cursor-not-allowed"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="min-w-0">
      <span className="block text-sm font-medium text-ink-900">{label}</span>
      <span className="mt-0.5 block text-sm text-neutral-700">{detail}</span>
    </span>
  </label>
);

export const NotificationsPanel: React.FC = () => {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const configured = isSupabaseConfigured();

  const [prefs, setPrefs] = useState<AlertPreferences>(ALERTS_OFF);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<keyof AlertPreferences | null>(null);
  const [error, setError] = useState<PanelError | null>(null);

  useEffect(() => {
    let cancelled = false;
    const workspaceId = workspace?.id;
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    void fetchAlertPreferences(workspaceId, t).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        setPrefs(result.data);
        setError(null);
      } else {
        // Not silently defaulted to all-off: that would show four unchecked
        // boxes for someone whose alerts are switched on, and they would read
        // that as "I am not subscribed".
        setError(result.error ? { kind: "message", text: result.error } : { kind: "load" });
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // `t` is deliberately NOT a dependency here, and the rule is silenced rather
    // than satisfied. This effect SETS STATE, and t's identity is not stable in
    // this component's test setup — including it made the effect re-run on its
    // own re-render, which is an infinite loop that blocks the event loop hard
    // enough that even a per-test timeout never fires. The only thing t does
    // inside is build a fallback error string; getting that string in a stale
    // language is a far smaller problem than a spinning page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id]);

  const toggle = useCallback(
    async (key: keyof AlertPreferences, next: boolean) => {
      const workspaceId = workspace?.id;
      if (!workspaceId) return;

      const previous = prefs;
      const optimistic = { ...prefs, [key]: next };
      setPrefs(optimistic);
      setSaving(key);
      setError(null);

      const result = await saveAlertPreferences(workspaceId, optimistic, t);
      setSaving(null);
      if (!result.success) {
        // Back to the stored truth. A switch left showing the user's intent
        // after a failed write is the screen telling a lie it cannot back.
        setPrefs(previous);
        setError(result.error ? { kind: "message", text: result.error } : { kind: "save" });
      }
    },
    [prefs, workspace?.id, t],
  );

  const disabled = !configured || loading || !workspace?.id || saving !== null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <Bell className="mt-0.5 h-5 w-5 flex-shrink-0 text-neutral-700" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink-900">
              {t("settings.notifications.title")}
            </h2>
            <p className="mt-1 text-sm text-neutral-700">
              {t("settings.notifications.subtitle", {
                name: workspace?.name ?? t("settings.notifications.thisWorkspace"),
              })}
            </p>
          </div>
        </div>

        {!configured && (
          <p className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm text-neutral-700">
            {t("settings.notifications.demoMode")}
          </p>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 px-4 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-error-600" />
            <p className="text-sm text-error-700">
              {error.kind === "message"
                ? error.text
                : t(`settings.notifications.${error.kind === "load" ? "loadFailed" : "saveFailed"}`)}
            </p>
          </div>
        )}

        <div className="mt-4 space-y-2">
          {ALERT_KEYS.map((key) => (
            <Toggle
              key={key}
              checked={prefs[key]}
              disabled={disabled}
              label={t(`settings.notifications.${SLUG[key]}Title`)}
              detail={t(`settings.notifications.${SLUG[key]}Detail`)}
              onChange={(next) => void toggle(key, next)}
            />
          ))}
        </div>

        {/* The anti-fatigue rule, said out loud. Someone deciding whether to
            switch this on is really asking "how often will this mail me?", and
            the honest answer — only when something is NEW — is the answer that
            makes them say yes. */}
        <p className="mt-4 border-t border-neutral-200 pt-4 text-sm text-neutral-700">
          {t("settings.notifications.cadence")}
        </p>
      </div>
    </div>
  );
};

export default NotificationsPanel;
